import React, { useState } from "react";
import { Button, Divider, Input, InputNumber, Modal, Segmented, Table, Tag, Upload, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import authApi from "../../../api/authApi";
import handleErrorAsync from "../../../utils/handleErrorAsync";

const MODE_OPTIONS = [
    { value: "same",    label: "Cùng số tiền" },
    { value: "perUser", label: "Mỗi user khác nhau" },
];

// Parse "userId:amount" or "userId,amount" lines into payList array
function parsePerUserInput(text) {
    return text
        .split(/[\n;]+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const parts = line.split(/[,:;\t]+/).map((s) => s.trim());
            return { userId: Number(parts[0]), amount: Number(parts[1]) };
        })
        .filter((item) => item.userId > 0 && item.amount > 0);
}

// Parse comma/newline separated userIds
function parseUserIds(text) {
    return text
        .split(/[,\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter((id) => id > 0 && Number.isInteger(id));
}

// Parse CSV file content into payList
function parseCsvContent(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const startIdx = isNaN(Number(lines[0]?.split(/[,;\t]/)[0]?.trim())) ? 1 : 0;
    return lines
        .slice(startIdx)
        .map((line) => {
            const parts = line.split(/[,;\t]+/).map((s) => s.trim());
            return { userId: Number(parts[0]), amount: Number(parts[1]) };
        })
        .filter((item) => item.userId > 0 && item.amount > 0);
}

const previewColumns = [
    { title: "User ID", dataIndex: "userId", key: "userId", width: 100 },
    {
        title: "Số tiền", dataIndex: "amount", key: "amount", align: "right",
        render: (v) => <span className="font-medium text-pink-600">{v.toLocaleString("vi-VN")}đ</span>,
    },
];

function BulkBonus() {
    const [mode, setMode]               = useState("same");
    const [userIdsText, setUserIdsText] = useState("");
    const [perUserText, setPerUserText] = useState("");
    const [bonusCash, setBonusCash]     = useState(null);
    const [title, setTitle]             = useState("");
    const [note, setNote]               = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isLoading, setIsLoading]     = useState(false);
    const [preview, setPreview]         = useState([]);

    // Real-time parsed count for live feedback
    const parsedIds   = parseUserIds(userIdsText);
    const parsedLines = parsePerUserInput(perUserText);

    const handlePreview = () => {
        let data = [];
        if (mode === "same") {
            if (parsedIds.length === 0)         return message.error("Vui lòng nhập ít nhất 1 User ID hợp lệ");
            if (!bonusCash || bonusCash <= 0)   return message.error("Số tiền thưởng phải lớn hơn 0");
            data = parsedIds.map((userId) => ({ userId, amount: bonusCash }));
        } else {
            if (parsedLines.length === 0) return message.error("Không có dữ liệu hợp lệ. Format: userId:amount mỗi dòng");
            data = parsedLines;
        }
        setPreview(data);
        setConfirmOpen(true);
    };

    const handleCsvUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result;
            const parsed = parseCsvContent(text);
            if (parsed.length === 0) {
                message.error("CSV không có dữ liệu hợp lệ. Cần cột: userId, amount");
                return;
            }
            setPerUserText(parsed.map((p) => `${p.userId}:${p.amount}`).join("\n"));
            setMode("perUser");
            message.success(`Đã import ${parsed.length} dòng từ CSV`);
        };
        reader.readAsText(file);
        return false; // prevent antd auto-upload
    };

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            let result;
            if (mode === "same") {
                result = await authApi.bonusMany({
                    userList:  parseUserIds(userIdsText),
                    bonusCash: bonusCash,
                    title:     title || "",
                    note:      note  || "",
                });
            } else {
                result = await authApi.bonusManyAdvanced({
                    payList: parsePerUserInput(perUserText),
                    title:   title || "",
                    note:    note  || "",
                });
            }
            const d = result?.data?.data;
            message.success(`Thành công: ${d?.successCount || 0} users. Bỏ qua: ${d?.skippedCount || 0}`);
            setUserIdsText(""); setPerUserText(""); setBonusCash(null); setTitle(""); setNote(""); setPreview([]);
            setConfirmOpen(false);
        } catch (error) {
            let msg = "Thất bại";
            handleErrorAsync(error, (m) => { msg = m; });
            message.error(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const totalAmount = preview.reduce((sum, p) => sum + p.amount, 0);

    return (
        <div className="flex flex-col gap-3 max-w-2xl">
            <div className="bg-surface border border-line rounded-lg p-5 shadow-sm flex flex-col gap-4">
                {/* Mode selector inside card */}
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-content-2">Chế độ thưởng</span>
                    <Segmented options={MODE_OPTIONS} value={mode} onChange={setMode} size="small" />
                </div>

                <Divider className="my-0" />

                {mode === "same" ? (
                    <>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium flex items-center gap-2">
                                Danh sách User ID
                                {parsedIds.length > 0 && (
                                    <Tag color="blue">{parsedIds.length} user</Tag>
                                )}
                            </label>
                            <Input.TextArea
                                value={userIdsText}
                                onChange={(e) => setUserIdsText(e.target.value)}
                                placeholder={"Nhập userId cách nhau bởi dấu phẩy hoặc xuống dòng\nVD: 123, 456, 789"}
                                rows={4}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium">Số tiền thưởng (cho tất cả)</label>
                            <InputNumber
                                value={bonusCash}
                                onChange={setBonusCash}
                                placeholder="Nhập số tiền"
                                style={{ width: "100%" }}
                                min={1}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                parser={(v) => v?.replace(/,/g, "")}
                                addonAfter="đ"
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium flex items-center gap-2">
                                Danh sách User ID : Số tiền
                                {parsedLines.length > 0 && (
                                    <Tag color="blue">{parsedLines.length} dòng</Tag>
                                )}
                            </label>
                            <Input.TextArea
                                value={perUserText}
                                onChange={(e) => setPerUserText(e.target.value)}
                                placeholder={"userId:amount mỗi dòng\nVD:\n123:50000\n456:100000"}
                                rows={6}
                                style={{ fontFamily: "monospace", fontSize: 13 }}
                            />
                        </div>

                        {/* CSV Drag & Drop upload */}
                        <Upload.Dragger
                            beforeUpload={handleCsvUpload}
                            accept=".csv"
                            showUploadList={false}
                            style={{ padding: "8px 0" }}
                        >
                            <p className="text-content-3 text-2xl mb-1">
                                <FontAwesomeIcon icon={faCloudArrowUp} />
                            </p>
                            <p className="text-sm text-content-2">Kéo thả hoặc click để upload CSV</p>
                            <p className="text-xs text-content-3 mt-1">Format: userId,amount (có hoặc không header)</p>
                        </Upload.Dragger>
                    </>
                )}

                <Divider className="my-0" />

                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Tiêu đề <span className="text-content-3 font-normal">(tuỳ chọn)</span></label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tiêu đề thông báo" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Nội dung <span className="text-content-3 font-normal">(tuỳ chọn)</span></label>
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nội dung thông báo" rows={2} />
                    </div>
                </div>

                <Button type="primary" onClick={handlePreview} block size="large">
                    Xem trước & Xác nhận
                </Button>
            </div>

            {/* Confirmation modal */}
            <Modal
                open={confirmOpen}
                title="Xác nhận thưởng tiền hàng loạt"
                onOk={onSubmit}
                onCancel={() => setConfirmOpen(false)}
                okText="Xác nhận thưởng"
                cancelText="Huỷ"
                okButtonProps={{ loading: isLoading }}
                width={520}
            >
                <div className="flex flex-col gap-3 py-2">
                    <div className="flex justify-between py-1 border-b border-line">
                        <span className="text-content-3">Tổng users</span>
                        <span className="font-semibold">{preview.length}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-line">
                        <span className="text-content-3">Tổng tiền</span>
                        <span className="font-semibold text-pink-600 text-base">{totalAmount.toLocaleString("vi-VN")}đ</span>
                    </div>
                    {title && (
                        <div className="flex justify-between py-1 border-b border-line">
                            <span className="text-content-3">Tiêu đề</span>
                            <span>{title}</span>
                        </div>
                    )}
                    <Table
                        dataSource={preview}
                        columns={previewColumns}
                        rowKey={(_, i) => i}
                        size="small"
                        pagination={{ pageSize: 10, size: "small" }}
                        scroll={{ y: 220 }}
                    />
                </div>
            </Modal>
        </div>
    );
}

export default BulkBonus;
